import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight,
  User as UserIcon, Robot, CheckCircle, Plus,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import ConfirmReviewPopup from "@/components/erp/ConfirmReviewPopup";

// ── Helpers ────────────────────────────────────────────────────────────────────

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

// ── Progress bar ───────────────────────────────────────────────────────────────

function ProgressBar({ progress }) {
  if (!progress) return null;
  const pctMatch  = progress.match(/\((\d+)%\)/);
  const stepMatch = progress.match(/(\d+)\/(\d+)/);
  if (!pctMatch || !stepMatch) return null;
  const pct           = parseInt(pctMatch[1], 10);
  const [, done, total] = stepMatch;
  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Step {done} of {total}</span>
        <span>{pct}% complete</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#0020C2] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// ── Captured fields (collapsible) ─────────────────────────────────────────────

function CapturedFieldsPanel({ fields }) {
  const entries = Object.entries(fields || {});
  if (!entries.length) return null;
  return (
    <details className="mx-4 mb-2 border border-gray-100 rounded-lg">
      <summary className="cursor-pointer text-xs font-bold text-gray-500 px-3 py-2">
        Captured so far ({entries.length})
      </summary>
      <div className="px-3 pb-2 space-y-1">
        {entries.map(([field, value]) => (
          <div key={field} className="flex justify-between text-xs border-t border-gray-50 pt-1">
            <span className="text-gray-400">{field.replace(/_/g, " ")}</span>
            <span className="font-mono text-gray-700 truncate max-w-[60%]">{String(value)}</span>
          </div>
        ))}
      </div>
    </details>
  );
}

// ── Searchable Legal Employer dropdown ─────────────────────────────────────────
// Renders inline inside the assistant chat bubble (same as SupplierAgentModal's
// SearchableChatOptions). Options come from the pre-fetched LOV state.

function LegalEmployerDropdown({ legalEmployers, onPick }) {
  const [q, setQ] = React.useState("");
  const filtered = q
    ? legalEmployers.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : legalEmployers;

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search legal employer…"
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
      />
      <div className="max-h-44 overflow-y-auto">
        {legalEmployers.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            Loading employers… (check Oracle connection)
          </div>
        )}
        {legalEmployers.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
        )}
        {filtered.slice(0, 60).map((opt) => (
          <div
            key={opt.value}
            onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
          >
            <span className="font-medium text-gray-800">{opt.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Radio / button options ─────────────────────────────────────────────────────

function RadioOptions({ fieldUi, onPick }) {
  return (
    <div className="mt-2 flex flex-wrap gap-1.5">
      {(fieldUi.options || []).map((opt) => (
        <button
          key={opt.value}
          onClick={() => onPick(opt)}
          className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors"
        >
          {opt.label}
        </button>
      ))}
    </div>
  );
}

// ── Markdown renderer ──────────────────────────────────────────────────────────

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p:      ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul:     ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol:     ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li:     ({ node, ...props }) => <li className="text-sm" {...props} />,
        }}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}

// ── Date picker widget ───────────────────────────────────────────────────────
// Renders an inline calendar date-picker in the chat bubble.
// The native <input type="date"> opens the OS date-picker on click.
// Sends the selected date as "field: YYYY-MM-DD" structured message.

function DatePickerWidget({ fieldUi, onPick }) {
  const [val, setVal] = React.useState("");
  const label = fieldUi?.label || fieldUi?.field?.replace(/_/g, " ") || "Date";
  return (
    <div className="mt-2 border border-[#0020C2]/30 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="bg-[#0020C2]/5 px-3 py-2 text-xs font-medium text-[#0020C2] border-b border-[#0020C2]/10">
        📅 Select {label}
      </div>
      <div className="px-3 py-2.5 flex items-center gap-2">
        <input
          type="date"
          value={val}
          onChange={(e) => setVal(e.target.value)}
          className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 outline-none focus:border-[#0020C2] focus:ring-1 focus:ring-[#0020C2]/20 bg-white cursor-pointer"
        />
        <button
          onClick={() => val && onPick(val)}
          disabled={!val}
          className="text-xs px-4 py-2 bg-[#0020C2] text-white rounded-lg disabled:opacity-40 font-medium hover:bg-[#0018A0] transition-colors"
        >
          Confirm
        </button>
      </div>
    </div>
  );
}

// ── Business Unit dropdown (inline in chat) ──────────────────────────────────
// Mirrors LegalEmployerDropdown — shows Oracle BU names from /business-units.
// The BU name itself is sent as the value (Oracle expects BusinessUnitName in POST).

function BusinessUnitDropdown({ businessUnits, loading, error, onPick }) {
  const [q, setQ] = React.useState("");
  const filtered = q
    ? businessUnits.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : businessUnits;

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search business unit…"
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
      />
      <div className="max-h-44 overflow-y-auto">
        {loading && (
          <div className="px-3 py-2 text-xs text-gray-400 flex items-center gap-1">
            <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin inline-block" />
            Loading Business Units from Oracle…
          </div>
        )}
        {!loading && error && (
          <div className="px-3 py-2 text-xs text-red-500">
            ⚠ {error}
          </div>
        )}
        {!loading && !error && businessUnits.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">
            No Business Units returned from Oracle.
          </div>
        )}
        {businessUnits.length > 0 && filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
        )}
        {filtered.slice(0, 60).map((opt) => (
          <div
            key={opt.value}
            onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
          >
            <span className="font-medium text-gray-800">{opt.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Generic LOV searchable dropdown (Position / Department / Job / Manager) ────────
function LovDropdown({ options, onPick, placeholder = "Search…", emptyMsg = "No options from Oracle." }) {
  const [q, setQ] = React.useState("");
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()) || (o.value || "").toLowerCase().includes(q.toLowerCase()))
    : options;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={placeholder}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="max-h-48 overflow-y-auto">
        {options.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">{emptyMsg}</div>
        )}
        {filtered.slice(0, 80).map((opt, i) => (
          <div key={opt.value + i} onClick={() => onPick(opt)}
            className="px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0">
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Country Code dropdown (inline in chat) ─────────────────────────────────────
function CountryCodeDropdown({ options, onPick }) {
  const [q, setQ] = React.useState("");
  const filtered = q
    ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()) || o.value.includes(q))
    : options;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search country or code…"
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="max-h-48 overflow-y-auto">
        {filtered.map((opt) => (
          <div key={opt.value} onClick={() => onPick(opt)}
            className="px-3 py-2 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0 flex justify-between">
            <span>{opt.label}</span>
            <span className="text-gray-400 font-mono">+{opt.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Grade dropdown (inline in chat) ───────────────────────────────────────────
function GradeDropdown({ gradeOptions, onPick }) {
  const [q, setQ] = React.useState("");
  const filtered = q
    ? gradeOptions.filter((o) => o.label.toLowerCase().includes(q.toLowerCase()))
    : gradeOptions;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search grades…"
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="max-h-44 overflow-y-auto">
        {gradeOptions.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No grades from Oracle — grade is optional.</div>
        )}
        {filtered.slice(0, 60).map((opt) => (
          <div key={opt.value} onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0">
            {opt.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Chat bubble ────────────────────────────────────────────────────────────────
// Reads next_field_ui from msg.result (the raw API response) to decide
// which widget to render below the assistant text.

function HRChatBubble({ msg, legalEmployers, businessUnits, buLoading, buError, genderOptions, maritalOptions, gradeOptions, positionOptions, allPositions, departmentOptions, jobOptions, filteredJobOptions, managerOptions, onOptionClick }) {
  const isUser  = msg.role === "user";
  // field_ui lives at msg.result.next_field_ui (the full API response object)
  const fieldUi = !isUser && !msg.loading ? (msg.result?.next_field_ui ?? null) : null;

  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      {/* Avatar */}
      <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white ${
        isUser ? "bg-[#0020C2]" : "bg-gray-700"
      }`}>
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>

      {/* Bubble */}
      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed ${
        isUser
          ? "bg-[#0020C2] text-white rounded-tr-sm"
          : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"
      }`}>
        {/* Loading dots */}
        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce"
                style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}

        {/* Message text */}
        {!msg.loading && msg.text && (
          isUser
            ? <p className="whitespace-pre-wrap">{msg.text}</p>
            : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}

        {/* Legal Employer searchable dropdown */}
        {fieldUi?.uiType === "searchable_dropdown" && fieldUi.field === "legal_employer_name" && (
          <LegalEmployerDropdown
            legalEmployers={legalEmployers}
            onPick={(opt) => onOptionClick?.(opt, "legal_employer_name")}
          />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && fieldUi.field === "country_code_number" && (
          <CountryCodeDropdown
            options={fieldUi.options || []}
            onPick={(opt) => onOptionClick?.({ value: opt.value, label: opt.label }, "country_code_number")}
          />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && fieldUi.field === "grade_code" && (
          <GradeDropdown
            gradeOptions={gradeOptions || []}
            onPick={(opt) => {
              const payload = `${opt.value}|${opt.grade_id || ""}`;
              onOptionClick?.({ value: payload, label: opt.label }, "grade_code");
            }}
          />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && fieldUi.field === "position_code" && (
          <LovDropdown options={positionOptions || []} placeholder="Search positions…"
            emptyMsg="No positions returned from Oracle — position is optional."
            onPick={(opt) => {
              // Send position code to agent; pass dept + job + BU for auto-override
              onOptionClick?.({
                value:     opt.value,
                label:     opt.label,
                _dept:     opt.department || "",
                _bu:       opt.bu         || "",   // position's BusinessUnitName — overrides BU selection
                _job_code: opt.job_code   || "",
                _job_name: opt.job_name   || "",
              }, "position_code");
            }} />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && fieldUi.field === "department_name" && (
          <LovDropdown
            options={departmentOptions.length > 0 ? departmentOptions : (positionOptions || []).filter(p => p.department).map(p => ({ label: p.department, value: p.department }))}
            placeholder="Search departments…"
            emptyMsg="No departments for the selected position."
            onPick={(opt) => onOptionClick?.({ value: opt.value, label: opt.label }, "department_name")} />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && fieldUi.field === "job_code" && (
          <LovDropdown
            options={filteredJobOptions.length > 0 ? filteredJobOptions : (jobOptions || [])}
            placeholder="Search jobs…"
            emptyMsg="No jobs for this position — job is optional."
            onPick={(opt) => onOptionClick?.({ value: opt.value, label: opt.label }, "job_code")} />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && fieldUi.field === "manager_assignment_id" && (
          <LovDropdown
            options={managerOptions || []}
            placeholder="Search managers…"
            emptyMsg="No managers available"
            onPick={(opt) => {
              // opt comes directly from the API response mapped by fetch_managers:
              // { label, value: ManagerAssignmentNumber, assignment_id: ManagerAssignmentId, manager_type: ManagerType }
              console.log("[ManagerLOV] selected:", opt);
              const payload = `${opt.value}|${opt.assignment_id || ""}|${opt.manager_type || ""}`;
              console.log("[ManagerLOV] pipe payload to agent:", payload);
              onOptionClick?.({ value: payload, label: opt.label }, "manager_assignment_id");
            }} />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && fieldUi.field === "business_unit_name" && (
          <BusinessUnitDropdown
            businessUnits={businessUnits}
            loading={buLoading}
            error={buError}
            onPick={(opt) => onOptionClick?.(opt, "business_unit_name")}
          />
        )}

        {/* Date picker */}
        {fieldUi?.uiType === "date_picker" && (
          <DatePickerWidget
            fieldUi={fieldUi}
            onPick={(dateStr) => {
              // Send as "field: value" so _parse_inline_fields maps to the correct field
              const structuredMsg = `${fieldUi.field}: ${dateStr}`;
              // Display just the date in the user bubble; backend gets structured form
              onOptionClick?.({ value: structuredMsg, label: dateStr, _display: dateStr }, fieldUi.field);
            }}
          />
        )}

        {/* Radio / button options for other fields */}
        {fieldUi?.uiType === "radio" && (() => {
          // Prefer live Oracle options fetched after employer selection over backend-embedded options
          const liveOpts = fieldUi.field === "gender" ? (genderOptions?.length ? genderOptions : fieldUi.options)
                         : fieldUi.field === "marital_status" ? (maritalOptions?.length ? maritalOptions : fieldUi.options)
                         : fieldUi.options;
          return liveOpts?.length > 0 ? (
            <RadioOptions fieldUi={{...fieldUi, options: liveOpts}} onPick={(opt) => onOptionClick?.(opt, fieldUi.field)} />
          ) : null;
        })()}

        {/* Generic button fallback */}
        {fieldUi && !["searchable_dropdown", "radio"].includes(fieldUi.uiType) && fieldUi.options?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {fieldUi.options.map((opt) => (
              <button key={opt.value}
                onClick={() => onOptionClick?.(opt, fieldUi.field)}
                className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors">
                {opt.label}
              </button>
            ))}
          </div>
        )}

        {/* Timestamp */}
        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>
            {msg.timestamp}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Input bar ──────────────────────────────────────────────────────────────────

function HRInputBar({ inputRef, value, onChange, onKeyDown, onSend, busy }) {
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50 focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        <textarea
          ref={inputRef}
          value={value}
          onChange={onChange}
          onKeyDown={onKeyDown}
          rows={1}
          disabled={busy}
          placeholder="Continue the conversation…"
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }}
        />
        <button
          onClick={onSend}
          disabled={busy || !value.trim()}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0"
        >
          {busy
            ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            : <PaperPlaneRight size={14} weight="fill" />
          }
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

// ── Wizard field panel (right side) ───────────────────────────────────────────

const WIZARD_FIELDS = [
  { key: "first_name",           label: "First Name",          required: true  },
  { key: "last_name",            label: "Last Name",           required: true  },
  { key: "legal_employer_name",  label: "Legal Employer",      required: true  },
  { key: "legislation_code",     label: "Legislation Code",    required: false },
  { key: "business_unit_name",   label: "Business Unit",       required: false },
  { key: "start_date",           label: "Start Date",          required: true  },
  { key: "date_of_birth",        label: "Date of Birth",       required: true  },
  { key: "gender",               label: "Gender",              required: true  },
  { key: "marital_status",       label: "Marital Status",      required: true  },
  { key: "email",                label: "Work Email",          required: true  },
  { key: "country_code_number",  label: "Country Code",        required: false },
  { key: "phone_number",         label: "Phone Number",        required: true  },
  { key: "address_line1",        label: "Address Line 1",      required: true  },
  { key: "city",                 label: "City",                required: true  },
  { key: "passport_number",      label: "Passport Number",     required: false },
  { key: "passport_issue_date",  label: "Passport Issue Date", required: false },
  { key: "passport_expiry_date", label: "Passport Expiry",     required: false },
];

function WizardPanel({ fieldValues, workerCreated, oracleResult, onChangeEmployer }) {
  return (
    <div className="flex flex-col bg-white h-full">
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5 mb-1">
          <Sparkle size={11} weight="fill" /> Wizard fields
          <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">
            · auto-filled as you chat
          </span>
        </div>

        {WIZARD_FIELDS.map(({ key, label, required }) => {
          const value  = fieldValues[key];
          const filled = Boolean(value);
          return (
            <div key={key} className="flex flex-col gap-0.5">
              <label className="text-[10px] font-medium text-gray-400 flex items-center gap-1">
                {label}
                {required && <span className="text-red-400">*</span>}
                {filled && <CheckCircle size={10} weight="fill" className="text-green-500 ml-auto" />}
              </label>
              <div className={`w-full text-xs rounded px-2 py-1.5 border ${
                filled
                  ? "border-green-200 bg-green-50/50 text-gray-800"
                  : "border-gray-100 bg-gray-50 text-gray-300"
              }`}>
                {filled ? value : "—"}
              </div>
              {/* Change Legal Employer button */}
              {key === "legal_employer_name" && filled && onChangeEmployer && !workerCreated && (
                <button
                  onClick={onChangeEmployer}
                  className="text-[10px] text-[#0020C2] hover:underline mt-0.5 text-left"
                >
                  Change Legal Employer
                </button>
              )}
            </div>
          );
        })}

        {workerCreated && oracleResult && (
          <div className="mt-4 p-3 bg-green-50 rounded-xl border border-green-100">
            <div className="flex items-center gap-1.5 text-green-700 font-bold text-sm mb-1">
              <CheckCircle size={14} weight="fill" /> Worker Created
            </div>
            {oracleResult.PersonNumber && (
              <div className="text-xs text-green-600">
                Person #: <strong>{oracleResult.PersonNumber}</strong>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main modal ─────────────────────────────────────────────────────────────────

const HR_QUICK_ACTIONS = ["Create a new worker", "Help"];

export default function HRWorkerAgentModal() {
  // const { openAgent, setOpenAgent, setSetupOpen, profile } = useErp();
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();
  // Follow SupplierAgentModal: display name comes from ErpContext agent catalog.
  const agent = agents?.find((a) => a.key === openAgent);

  const [sessionId,      setSessionId]      = React.useState(null);
  const [messages,       setMessages]       = React.useState([]);
  const [input,          setInput]          = React.useState("");
  const [busy,           setBusy]           = React.useState(false);
  const [chatStarted,    setChatStarted]    = React.useState(false);
  const [progress,       setProgress]       = React.useState(null);
  const [fieldValues,    setFieldValues]    = React.useState({});
  const [workerCreated,  setWorkerCreated]  = React.useState(false);
  const [oracleResult,   setOracleResult]   = React.useState(null);
  const [legalEmployers, setLegalEmployers] = React.useState([]);
  const [businessUnits,  setBusinessUnits]  = React.useState([]);
  const [genderOptions,  setGenderOptions]  = React.useState([]);
  const [gradeOptions,      setGradeOptions]      = React.useState([]);
  const [positionOptions,   setPositionOptions]   = React.useState([]);
  const [allPositions,      setAllPositions]      = React.useState([]);  // full dataset incl. bu_id
  const [selectedBuId,      setSelectedBuId]      = React.useState(null); // BusinessUnitId of selected BU
  const [departmentOptions, setDepartmentOptions] = React.useState([]);
  const [jobOptions,        setJobOptions]        = React.useState([]);  // all jobs from /jobs
  const [filteredJobOptions, setFilteredJobOptions] = React.useState([]); // jobs for selected position
  const [managerOptions,    setManagerOptions]    = React.useState([]);
  const [maritalOptions, setMaritalOptions] = React.useState([]);
  const [buLoading,      setBuLoading]      = React.useState(false);
  const [buError,        setBuError]        = React.useState(null);
  const [readyToConfirm, setReadyToConfirm] = React.useState(false);
  const [showConfirmPopup, setShowConfirmPopup] = React.useState(false);

  const messagesEndRef = React.useRef(null);
  const inputRef       = React.useRef(null);

  // Auto-scroll
  React.useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ── On open: create session (no LLM) + pre-fetch Legal Employer LOV ────────
  React.useEffect(() => {
    if (openAgent !== "create-hr") return;

    // Full reset
    setMessages([]);
    setInput("");
    setProgress(null);
    setFieldValues({});
    setWorkerCreated(false);
    setOracleResult(null);
    setLegalEmployers([]);
    setBusinessUnits([]);
    setGenderOptions([]);
    setMaritalOptions([]);
    setBuLoading(false);
    setBuError(null);
    setReadyToConfirm(false);
    setShowConfirmPopup(false);
    setChatStarted(false);
    setSessionId(null);

    let cancelled = false;

    api.post("/erp-agents/hr-worker-create/start")
      .then(({ data }) => { if (!cancelled) setSessionId(data.session_id); })
      .catch((err) => console.error("[HR] /start failed:", err));

    api.get("/erp-agents/hr-worker-create/legal-employers")
      .then(({ data }) => { if (!cancelled) setLegalEmployers(data.employers || []); })
      .catch((err) => console.error("[HR] /legal-employers failed:", err));

    api.get("/erp-agents/hr-worker-create/grade-options")
      .then(({ data }) => { if (!cancelled) setGradeOptions(data.grade_options || []); })
      .catch(() => setGradeOptions([]));
    api.get("/erp-agents/hr-worker-create/positions")
      .then(({ data }) => {
        if (!cancelled) {
          const opts = data.options || [];
          setPositionOptions(opts);
          setAllPositions(opts);  // keep full dataset for department filtering
        }
      })
      .catch(() => { setPositionOptions([]); setAllPositions([]); });
    // Departments are derived from position selection — no separate fetch needed
    api.get("/erp-agents/hr-worker-create/jobs")
      .then(({ data }) => { if (!cancelled) setJobOptions(data.options || []); })
      .catch(() => setJobOptions([]));
    api.get("/erp-agents/hr-worker-create/managers")
      .then(({ data }) => {
        if (!cancelled) {
          const opts = data.options || [];
          console.log("[ManagerLOV] raw API response:", data);
          console.log("[ManagerLOV] mapped options (" + opts.length + "):", opts);
          setManagerOptions(opts);
        }
      })
      .catch((err) => { console.error("[ManagerLOV] fetch error:", err); setManagerOptions([]); });

    return () => { cancelled = true; };
  }, [openAgent]);

  // ── Core message sender ────────────────────────────────────────────────────
  // displayText: what to show in the user bubble (defaults to the actual message)
  // backendMsg:  what to send to the backend (defaults to displayText)
  const sendMessage = React.useCallback(async (displayText, backendMsg) => {
    const userText    = (displayText || "").trim();
    const serverText  = (backendMsg  || displayText || "").trim();
    if (!userText || busy || !sessionId) return;

    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";
    if (!chatStarted) setChatStarted(true);

    const isInternalSignal = serverText === "__confirm__" || serverText === "__edit__" || serverText === "__change_employer__";
    setMessages((prev) => [
      ...prev,
      // Internal button signals produce no user bubble — only the assistant response
      ...(isInternalSignal ? [] : [{ id: Date.now(), role: "user", text: userText, timestamp: ts() }]),
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);

    try {
      const { data } = await api.post("/erp-agents/hr-worker-create/chat", {
        session_id: sessionId,
        message:    serverText,
      });

      if (data.progress)        setProgress(data.progress);
      if (data.fields_collected) setFieldValues((prev) => ({ ...prev, ...data.fields_collected }));
      if (data.worker_created)  {
        setWorkerCreated(true); setOracleResult(data.oracle_response);
        setReadyToConfirm(false); setShowConfirmPopup(false);
      } else if (data.ready_to_confirm) {
        setReadyToConfirm(true); setShowConfirmPopup(true);
      } else {
        setReadyToConfirm(false);
      }

      const reply = data.reply || "";
      const now   = ts();
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, text: reply, result: data, timestamp: now }
            : m
        )
      );
    } catch (err) {
      const detail = err?.response?.data?.detail || err.message || "Unknown error";
      setMessages((prev) =>
        prev.map((m) =>
          m.loading
            ? { ...m, loading: false, text: `Error: ${detail}`, timestamp: ts() }
            : m
        )
      );
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [busy, sessionId, chatStarted]);

  // ── Employer selection ─────────────────────────────────────────────────────
  // displayText = employer name shown in user bubble (clean, human-readable)
  // backendMsg  = structured key:value string that agent.py parses via _parse_inline_fields
  const handleOptionClick = React.useCallback((opt, fieldName) => {
    if (fieldName === "legal_employer_name") {
      const name    = opt.name || opt.value;
      const legCode = opt.legislation_code || "";

      // Reflect employer + legislation in wizard immediately; clear stale BU/gender/marital
      setFieldValues((prev) => ({
        ...prev,
        legal_employer_name: name,
        legislation_code:    legCode || prev.legislation_code || "",
        business_unit_name:  "",   // must be re-selected from BU LOV — never default to employer name
        gender:              "",
        marital_status:      "",
      }));
      // Reset BU id and downstream position/dept/job when employer changes
      setSelectedBuId(null);
      setPositionOptions([]);
      setDepartmentOptions([]);
      setFilteredJobOptions([]);
      // Clear stale BU, show loading state, reset error
      setBusinessUnits([]);
      setBuLoading(true);
      setBuError(null);

      // Fetch BU LOV in parallel with sending the employer selection message
      const backendMsg = [
        `legal_employer_name: ${name}`,
        legCode ? `legislation_code: ${legCode}` : "",
      ].filter(Boolean).join(", ");

      // Fetch gender + marital options valid for this legislation
      const legParam = legCode ? `?legislation_code=${encodeURIComponent(legCode)}` : "";
      api.get(`/erp-agents/hr-worker-create/gender-options${legParam}`)
        .then(({ data }) => setGenderOptions(data.options || []))
        .catch(() => setGenderOptions([]));
      api.get(`/erp-agents/hr-worker-create/marital-status-options${legParam}`)
        .then(({ data }) => setMaritalOptions(data.options || []))
        .catch(() => setMaritalOptions([]));

      api.get("/erp-agents/hr-worker-create/business-units")
        .then(({ data }) => {
          if (data.error) {
            setBuError(data.error);
            setBusinessUnits([]);
          } else {
            setBusinessUnits(data.business_units || []);
            setBuError(null);
          }
        })
        .catch((err) => {
          setBuError("Failed to load Business Units — check Oracle connection.");
          setBusinessUnits([]);
        })
        .finally(() => setBuLoading(false));

      sendMessage(name, backendMsg);
    } else if (fieldName === "business_unit_name") {
      // Store BusinessUnitId so we can filter positions to only those in this BU
      const buId = opt.id || null;
      setSelectedBuId(buId);
      // Reset all downstream LOV state when BU changes
      setPositionOptions([]);
      setDepartmentOptions([]);
      setFilteredJobOptions([]);
      // Filter allPositions to only rows whose bu_id matches the selected BU id
      if (buId && allPositions.length > 0) {
        const filtered = allPositions.filter((p) => String(p.bu_id) === String(buId));
        setPositionOptions(filtered);
        console.log(`[BU→Position] BU id=${buId} → ${filtered.length} / ${allPositions.length} positions`);
      } else {
        // No id available — fall back to filtering by BU name
        const buName = opt.value || opt.label;
        const filtered = allPositions.filter((p) => p.bu === buName);
        setPositionOptions(filtered.length > 0 ? filtered : allPositions);
        console.log(`[BU→Position] fallback name filter "${buName}" → ${filtered.length} positions`);
      }
      sendMessage(opt.label || opt.value, opt.value);
    } else {
      // For date pickers: opt._display is the clean display text, opt.value is the structured msg.
      // For radio/other: opt.value is both display and backend message.
      const display = opt._display || opt.label || opt.value;
      const backend = opt.value;
      sendMessage(display, backend);
      // If a position was selected and it has a department, auto-fill department
      if (fieldName === "position_code") {
        // Auto-populate department from the same positionsLov row
        if (opt._dept) {
          setDepartmentOptions([{ label: opt._dept, value: opt._dept }]);
        }
        // Filter jobs: use job from the same position row if available,
        // else fall back to full jobOptions list
        if (opt._job_code && opt._job_name) {
          setFilteredJobOptions([{ label: opt._job_name, value: opt._job_code }]);
        } else {
          setFilteredJobOptions([]);  // fallback: job dropdown shows all jobOptions
        }
        // Override BusinessUnitName with the position's own BU.
        // Oracle requires the position and the assignment BU to match (PER-1532316).
        if (opt._bu) {
          // Send as structured override so agent stores it in state before the POST
          sendMessage(`__bu_override__:${opt._bu}`, `__bu_override__:${opt._bu}`);
        }
      }
    }
  }, [sendMessage]);

  // ── Change Legal Employer: sends a structured signal that clears the employer
  // fields in state and re-emits the Legal Employer dropdown in the chat.
  const handleChangeEmployer = React.useCallback(() => {
    sendMessage("__change_employer__", "__change_employer__");
  }, [sendMessage]);

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); }
  };

  const startNewChat = () => {
    setMessages([]);
    setInput("");
    setProgress(null);
    setFieldValues({});
    setWorkerCreated(false);
    setOracleResult(null);
    setBusinessUnits([]);
    setGenderOptions([]);
    setMaritalOptions([]);
    setBuLoading(false);
    setBuError(null);
    setReadyToConfirm(false);
    setShowConfirmPopup(false);
    setChatStarted(false);
    api.post("/erp-agents/hr-worker-create/start")
      .then(({ data }) => setSessionId(data.session_id))
      .catch(console.error);
  };

  if (openAgent !== "create-hr") return null;

  return (
    <>
      <style>{`
        @keyframes chatFadeIn    { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes wizardSlideIn { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        .hr-modal-pre  { width:min(92vw,900px); max-height:90vh; }
        .hr-modal-chat { width:min(92vw,1360px); height:90vh; }
      `}</style>

      <div
        className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4"
        onClick={() => { if (!chatStarted) setOpenAgent(null); }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden
            ${chatStarted ? "hr-modal-chat" : "hr-modal-pre"}`}
          style={{ transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">
                Oracle HCM · Vaku AI
              </div>
              {/* <h3 className="text-2xl font-black tracking-tight mt-0.5">Create HR Worker</h3> */}
              <h3 className="text-2xl font-black tracking-tight mt-0.5">{agent?.label}</h3>
              <p className="text-xs text-gray-500 mt-0.5">Create a new worker record in Oracle HCM</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {chatStarted && (
                <>
                  <button
                    onClick={startNewChat}
                    className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
                  >
                    <Plus size={13} weight="bold" /> New Chat
                  </button>
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button
                onClick={() => { setOpenAgent(null); setSetupOpen(true); }}
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0"
                title="Oracle Settings"
              >
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Oracle Settings"}
              </button>
              <div className="w-px h-5 bg-gray-200" />
              {workerCreated && (
                <span className="flex items-center gap-1 text-xs text-green-600 font-medium bg-green-50 px-2 py-1 rounded-full">
                  <CheckCircle size={11} weight="fill" /> Created
                </span>
              )}
              <button onClick={() => setOpenAgent(null)}><X size={18} /></button>
            </div>
          </div>

          {/* Body */}
          {!chatStarted ? (
            /* Pre-chat — Wizard Fields so user sees what will be collected */
            <>
              <div className="flex flex-1 overflow-hidden">
                <div className="flex-1 overflow-y-auto p-5">
                  <WizardPanel
                    fieldValues={fieldValues}
                    workerCreated={workerCreated}
                    oracleResult={oracleResult}
                    onChangeEmployer={handleChangeEmployer}
                  />
                </div>
              </div>
              <HRInputBar inputRef={inputRef} value={input} onChange={handleInputChange}
                onKeyDown={handleKeyDown} onSend={() => sendMessage(input)} busy={busy} />
            </>
          ) : (
            /* Chat mode: 60% chat / 40% wizard */
            <div className="flex flex-1 overflow-hidden">

              {/* LEFT — chat */}
              <div className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{ width: "60%", animation: "chatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}>

                {/* Progress bar removed — wizard panel on the right shows field completion */}

                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => (
                    <HRChatBubble
                      key={msg.id}
                      msg={msg}
                      legalEmployers={legalEmployers}
                      businessUnits={businessUnits}
                      buLoading={buLoading}
                      buError={buError}
                      genderOptions={genderOptions}
                      maritalOptions={maritalOptions}
                      gradeOptions={gradeOptions}
                      positionOptions={positionOptions}
                      allPositions={allPositions}
                      departmentOptions={departmentOptions}
                      jobOptions={jobOptions}
                      filteredJobOptions={filteredJobOptions}
                      managerOptions={managerOptions}
                      onOptionClick={handleOptionClick}
                    />
                  ))}
                  <div ref={messagesEndRef} />
                </div>

                <HRInputBar inputRef={inputRef} value={input} onChange={handleInputChange}
                  onKeyDown={handleKeyDown} onSend={() => sendMessage(input)} busy={busy} />
              </div>

              {/* RIGHT — wizard */}
              <div className="overflow-hidden"
                style={{ width: "40%", minWidth: "300px", animation: "wizardSlideIn 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
                <WizardPanel fieldValues={fieldValues} workerCreated={workerCreated} oracleResult={oracleResult} onChangeEmployer={handleChangeEmployer} />
              </div>

            </div>
          )}
        </div>
      </div>

      {/* ── ConfirmReviewPopup — shown as overlay when all fields collected ── */}
      {showConfirmPopup && !workerCreated && (
        <ConfirmReviewPopup
          title="Create HR Worker"
          subtitle="Review the details before creating the worker record in Oracle HCM."
          confirmLabel="Create Worker"
          cancelLabel="Review / Edit"
          busy={busy}
          onConfirm={() => { setShowConfirmPopup(false); sendMessage("__confirm__", "__confirm__"); }}
          onCancel={() => { setShowConfirmPopup(false); sendMessage("__edit__", "__edit__"); }}
          fields={[
            { label: "First Name",     value: fieldValues.first_name           || "—" },
            { label: "Last Name",      value: fieldValues.last_name            || "—" },
            { label: "Legal Employer", value: fieldValues.legal_employer_name  || "—" },
            { label: "Business Unit",  value: fieldValues.business_unit_name   || "—" },
            { label: "Start Date",     value: fieldValues.start_date           || "—", highlight: true },
            { label: "Date of Birth",  value: fieldValues.date_of_birth        || "—" },
            { label: "Gender",         value: fieldValues.gender               || "—" },
            { label: "Marital Status", value: fieldValues.marital_status       || "—" },
            { label: "Email",          value: fieldValues.email                || "—" },
            { label: "Country Code",  value: fieldValues.country_code_number  || "—" },
            { label: "Phone",          value: fieldValues.phone_number         || "—" },
            { label: "Address",        value: fieldValues.address_line1        || "—" },
            { label: "City",           value: fieldValues.city                 || "—" },
          ]}
        />
      )}
    </>
  );
}
